import type { ViewableDocument } from '@govinity/ui/components/ui-custom/documents-viewer';
import type {
  LegislativeItemSimpleAttachment,
  MeetingEventPublicAgendaSection,
  MeetingEventPublicDocument,
} from '@govinity/validation/public';

import type { TranslatableDocument } from '@/components/ui/translatable-documents';

/** A document the agenda reader can open, keyed so the navigation can mark the open one. */
export type AgendaDocument = {
  key: string;
  document: ViewableDocument & TranslatableDocument;
  title: string;
  /**
   * Meeting-level documents translate through the meeting endpoint this tab's provider is wired to.
   * Item attachments belong to a legislative item and are requested against a different endpoint,
   * so opening one must not ask this provider for a translation it cannot serve.
   */
  canRequestTranslation: boolean;
};

export type AgendaNavItem = {
  documents: AgendaDocument[];
  id: number;
  number: string;
  title: string;
};

export type AgendaNavSection = {
  children: AgendaNavSection[];
  displayChildSectionsFirst: boolean;
  id: number;
  items: AgendaNavItem[];
  number: string;
  title: string;
};

const trimTrailingDot = (number: string) => number.replace(/\.$/, '');

export const meetingDocumentKey = (documentId: number) => `meeting-document-${documentId}`;

export const attachmentKey = (agendaSectionItemId: number, attachmentId: number) =>
  `attachment-${agendaSectionItemId}-${attachmentId}`;

export function meetingDocument(
  document: MeetingEventPublicDocument,
  title: string
): AgendaDocument {
  return { key: meetingDocumentKey(document.id), document, title, canRequestTranslation: true };
}

function attachmentDocument(
  attachment: LegislativeItemSimpleAttachment,
  agendaSectionItemId: number
): AgendaDocument {
  return {
    key: attachmentKey(agendaSectionItemId, attachment.id),
    document: attachment,
    title: attachment.attachmentNumber
      ? `${attachment.attachmentNumber} ${attachment.name}`
      : attachment.name,
    canRequestTranslation: false,
  };
}

function toNavSection(
  section: MeetingEventPublicAgendaSection,
  sectionNumber: string
): AgendaNavSection {
  return {
    displayChildSectionsFirst: section.displayChildSectionsFirst ?? false,
    id: section.id,
    number: sectionNumber,
    title: section.title,
    items: (section.legislativeItems ?? []).map((item, index) => {
      const number = item.itemNumber || `${trimTrailingDot(sectionNumber)}.${index + 1}`;
      const title = item.shortTitle || item.fullTitle;

      return {
        // Only PDFs open in the reader; every other format can only be downloaded
        documents: (item.attachments ?? [])
          .filter(({ extension }) => extension.toLowerCase() === 'pdf')
          .map((attachment) => attachmentDocument(attachment, item.agendaSectionItemId)),
        id: item.agendaSectionItemId,
        number,
        title,
      };
    }),
    children: (section.childSections ?? []).map((child, index) =>
      toNavSection(child, child.sectionNumber || `${trimTrailingDot(sectionNumber)}.${index + 1}.`)
    ),
  };
}

/**
 * Flattens the agenda into the tree the reader's navigation renders. Numbering repeats what
 * `AgendaView` derives — stored outline numbers where the government configured them, positional
 * fallbacks otherwise — so a document is labelled identically in the navigation and the agenda body.
 */
export function buildAgendaNav(sections: MeetingEventPublicAgendaSection[]): AgendaNavSection[] {
  return sections.map((section, index) =>
    toNavSection(section, section.sectionNumber || `${index + 1}.`)
  );
}

/**
 * Resolves a document key back to the document. Keys, rather than the documents themselves, are
 * what the page holds while a document is open: signed URLs are reissued on every meeting refetch,
 * so the open document has to be re-read from the current query data on each render.
 */
export function indexAgendaDocuments(
  navSections: AgendaNavSection[],
  meetingDocuments: AgendaDocument[]
): Map<string, AgendaDocument> {
  const index = new Map(meetingDocuments.map((document) => [document.key, document]));

  const collect = (sections: AgendaNavSection[]) => {
    for (const section of sections) {
      for (const item of section.items) {
        for (const document of item.documents) {
          index.set(document.key, document);
        }
      }
      collect(section.children);
    }
  };

  collect(navSections);

  return index;
}
